import { useState, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import { css } from '@emotion/react';
import { useQuery, useApolloClient } from '@apollo/client';
import { useUser } from '../providers/UserProvider';
import { useLabels } from '../providers/LabelsProvider';
import { colors, spacing } from '../config';
import { OverlayPortal } from './OverlayPortal';
import { Spinner } from './spinner/Spinner';
import { routes } from './RouteHandler';
import { LoginLayout } from '../layouts/LoginLayout';
import userSvg from 'url:../assets/lego_user.svg';
import { GET_CUSTOMERS } from '../queries/user';
import { GET_SURVEY_LIST } from '../queries/survey';
import { userHasNoCustomersKey } from '../constants/localStorageKeys';
import {
  addCustomBreadcrumb,
  addDefaultBreadcrumb,
  throwErrorWithTransactionName,
} from '../utils/elasticApmHeler';

const listStyle = css({
  maxHeight: 320,
  overflow: 'hidden',
  overflowY: 'scroll',
  listStyle: 'none',
  paddingRight: spacing.size2,
});

const listItemStyle = css({
  display: 'flex',
  alignItems: 'center',
  minHeight: 64,
  borderBottom: `2px solid ${colors.neutralColors.slate20}`,
  backgroundColor: colors.neutralColors.white,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: colors.neutralColors.slate05,
  },
});

const iconStyle = css({
  marginRight: spacing.size4,
});

const textStyle = css({
  color: colors.boldColors.lightBlue.base,
});

const MultiCustomerLoginPage = () => {
  addDefaultBreadcrumb('Customers loading', 'CustomerLoginPage');
  const { selectedCustomer, handleSelectedCustomerUpdate, signOut } = useUser();
  const client = useApolloClient();

  const {
    labels: { svy_multi_customer_login_heading },
  } = useLabels();

  addDefaultBreadcrumb('Query customers', 'CustomerLoginPage');
  const {
    data: customersData,
    loading: getCustomersLoading,
    error: getCustomersError,
  } = useQuery(GET_CUSTOMERS);

  // Sold-tos with at least one active assessment, resolved via GET_SURVEY_LIST; null while still checking
  const [activeCustomers, setActiveCustomers] = useState(null);
  const [activeCustomersError, setActiveCustomersError] = useState(null);

  const customerIds = customersData?.getCustomers
    ?.map((customer) => customer.id)
    .join(',');

  useEffect(() => {
    const customers = customersData?.getCustomers;

    // Only reset when there's stale data to clear, to avoid an extra render pass
    if (activeCustomers !== null) setActiveCustomers(null);
    if (activeCustomersError !== null) setActiveCustomersError(null);

    if (!customers?.length) return undefined;

    let isCancelled = false;

    async function filterActiveCustomers() {
      try {
        const results = await Promise.all(
          customers.map((customer) =>
            client.query({
              query: GET_SURVEY_LIST,
              variables: { customerId: customer.id },
              fetchPolicy: 'network-only',
            })
          )
        );

        if (isCancelled) return;

        setActiveCustomers(
          customers.filter((_, index) => {
            const surveyList = results[index].data?.getSurveyList ?? [];
            return surveyList.some((survey) => survey.active);
          })
        );
      } catch (error) {
        if (!isCancelled) setActiveCustomersError(error);
      }
    }

    filterActiveCustomers();

    return () => {
      isCancelled = true;
    };
    // customersData is represented by the stable customerIds key
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, customerIds]);

  if (selectedCustomer.id) {
    addDefaultBreadcrumb(
      'Customer ID selected',
      'CustomerLoginPage.selectedCustomer'
    );
    return <Redirect to={`/${routes.terms}`} />;
  }

  if (getCustomersLoading) {
    return (
      <OverlayPortal>
        <Spinner />
      </OverlayPortal>
    );
  }

  // Error boundary catches this error
  if (getCustomersError) {
    throwErrorWithTransactionName(getCustomersError, 'MultiCustomerLoginPage');
  }

  if (customersData && !customersData.getCustomers.length) {
    addDefaultBreadcrumb(
      'No customers found for user',
      'CustomerLoginPage.customerData'
    );
    window.localStorage.setItem(userHasNoCustomersKey, true);
    signOut();

    return (
      <OverlayPortal>
        <Spinner />
      </OverlayPortal>
    );
  }

  // Error boundary catches this error
  if (activeCustomersError) {
    throwErrorWithTransactionName(
      activeCustomersError,
      'MultiCustomerLoginPage'
    );
  }

  if (activeCustomers === null) {
    return (
      <OverlayPortal>
        <Spinner />
      </OverlayPortal>
    );
  }

  if (!activeCustomers.length) {
    addDefaultBreadcrumb(
      'No sold-to with an active assessment found for user',
      'CustomerLoginPage.customerData'
    );
    return <Redirect to={`/${routes.noAssessments}`} />;
  }

  if (activeCustomers.length === 1) {
    const theOnlyCustomer = activeCustomers[0];

    addCustomBreadcrumb({
      message: 'Single active customer found for user',
      category: 'CustomerLoginPage.customerData',
      data: { id: theOnlyCustomer.id },
    });
    handleSelectedCustomerUpdate({
      id: theOnlyCustomer.id,
      name: theOnlyCustomer.name,
    });
  }

  return (
    <LoginLayout title={svy_multi_customer_login_heading}>
      <ul css={listStyle}>
        {activeCustomers.map((customer) => (
          <li
            key={`customer-${customer.id}`}
            css={listItemStyle}
            onClick={() =>
              handleSelectedCustomerUpdate({
                id: customer.id,
                name: customer.name,
              })
            }
          >
            <img src={userSvg} alt="User icon" css={iconStyle} />
            <span css={textStyle}>{`${customer.id} - ${customer.name}`}</span>
          </li>
        ))}
      </ul>
    </LoginLayout>
  );
};

export { MultiCustomerLoginPage };
